import React, { useEffect, useState } from 'react'
import lang from 'lang'
import { ModalBig } from '@/modules/base/components/modal'
import { CancelButton, ButtonIconSquare } from '@/modules/base/components/buttons'
import { Checkbox, TextInput } from '@/modules/base/components/inputs'
import { makeStyles } from '@mui/styles'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import PropTypes from 'prop-types'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { AccessSection } from '@/modules/base/components/modal/access-modal'
import AccessUser from '@/modules/base/components/modal/access-modal/accessUser'
import { useSelector } from 'react-redux'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { user } from 'ampersand-app'
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined'

const useStyles = makeStyles((theme) => ({
  modal: {
    width: 760,
    maxWidth: 'calc(100vw - 48px)',
    borderRadius: 20,
  },
  actions: {
    padding: CssHelper.px(20, 32, 24, 32),
    gap: 10,
  },
  cancelButton: {
    minWidth: 110,
  },
  confirmButton: {
    minWidth: 112,
  },
  input: {
    margin: CssHelper.px(28, 0, 24, 0),
  },
  sharingWrapper: {
    marginTop: 4,
    marginBottom: 8,
  },
  accessSectionWrapper: {
    marginTop: 4,
  },
  shareCheckbox: {
    marginBottom: 0,
  },
  shareCheckboxVisual: {
    marginRight: 10,
  },
  checkboxContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  checkboxHeading: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 24,
    gap: 8,
  },
  checkboxIcon: {
    marginLeft: 0,
    marginTop: -1,
    fontSize: 18,
    color: theme.primary,
  },
  checkboxLabel: {
    marginLeft: 0,
    fontWeight: theme.fontWeights.bold,
    color: theme.text,
    lineHeight: '24px',
  },
  checkboxDescription: {
    marginLeft: CssHelper.px(25),
    color: theme.text,
    maxWidth: 520,
    lineHeight: 1.2,
  },
}))

const CreateFolderModal = (props) => {
  const classes = useStyles(props)

  const {
    title,
    open,
    onSubmit,
    onClose,
    value,
    confirmButtonLabel,
    emptyErrorMessage,
    invalidCharacters,
  } = props

  const [_value, setValue] = useState(value)
  const [isShared, setIsShared] = useState(false)
  const [newUser, setNewUser] = useState(null)

  const userInfo = useSelector(state => state.user.info)
  const [accessList, setAccessList] = useState([
    { id: userInfo.id, type: 'owner', name: userInfo.email },
  ])
  const isOpenFgaEnabled = user.experimentalFeatures.includes(experimentalFeatures.folderPermissions)
  const shareDescriptionText = 'Ordner wird den ausgewählten Benutzer freigegeben. Die Freigabe kann nach der Ordnerstellung bearbeitet werden'

  useEffect(() => {
    if (isOpenFgaEnabled) {
      setAccessList([
        { id: userInfo.id, type: 'owner', name: userInfo.email },
      ])
    } else {
      setAccessList([])
    }
  }, [isShared])

  const close = (e) => {
    e.stopPropagation()
    onClose(e)
  }

  const onConfirmButton = (e) => {
    e.stopPropagation()
    e.preventDefault()
    submit(_value)
  }

  const submit = (v) => {
    if (!v && emptyErrorMessage) {
      return toast.showToast({ type: 'error', message: emptyErrorMessage })
    }
    onSubmit(v, accessList)
  }

  const onPermissionCreate = () => {
    setAccessList((currentAccessList) => {
      if (currentAccessList.some((item) => item.id === newUser.id && item.type === 'editor')) {
        return currentAccessList
      }

      return [...currentAccessList, { id: newUser.id, type: 'editor', name: newUser.name }]
    })
  }

  const onPermissionDelete = (id) => {
    setAccessList(accessList.filter((item) => item.id !== id))
  }

  const onDeleteAll = () => {
    setAccessList(accessList.filter((item) => item.type === 'owner'))
  }

  const onAddAll = (users) => {
    setAccessList((currentAccessList) => {
      const existingEditorIds = new Set(
        currentAccessList
          .filter((item) => item.type === 'editor')
          .map((item) => String(item.id)),
      )

      const usersToAdd = users
        .filter((item) => !existingEditorIds.has(String(item.id)))
        .map((item) => ({ id: item.id, type: 'editor', name: item.name }))

      if (!usersToAdd.length) {
        return currentAccessList
      }

      return [...currentAccessList, ...usersToAdd]
    })
  }

  const buttons = [
    <CancelButton
      key='cancel'
      type='cancel'
      customClass={classes.cancelButton}
      label={lang.cancelButtonLabel}
      onClick={close} />,
    <ButtonIconSquare
      key='confirm'
      type='submit'
      customClass={classes.confirmButton}
      label={confirmButtonLabel ?? lang.confirmButtonLabel}
      onClick={onConfirmButton} />,
  ]

  return (
    <ModalBig
      title={title}
      actions={buttons}
      className={classes.modal}
      actionClass={classes.actions}
      showCloseIcon={false}
      open={open}
      onClose={close}>
      <TextInput label={`${lang.documents.folder.name} *`} className={classes.input} autoFocus={true} useFormik={false} value={_value}
        invalidCharacters={invalidCharacters} onChange={setValue} onEnter={onConfirmButton} />
      {isOpenFgaEnabled && (
        <div className={classes.sharingWrapper}>
          <Checkbox
            className={classes.shareCheckbox}
            customVisualClass={classes.shareCheckboxVisual}
            checked={isShared}
            inputPosition='top'
            onChange={() => setIsShared(!isShared)}
          >
            <div className={classes.checkboxContent}>
              <div className={classes.checkboxHeading}>
                <PersonAddAlt1OutlinedIcon fontSize='small' className={classes.checkboxIcon} />
                <span className={classes.checkboxLabel}>{lang.documents.folder.createShared}</span>
              </div>
              <div className={classes.checkboxDescription}>{shareDescriptionText}</div>
            </div>
          </Checkbox>
        </div>
      )}
      {isShared && (
        <div className={classes.accessSectionWrapper}>
          <AccessSection
            newUser={newUser}
            setNewUser={setNewUser}
            onCreate={onPermissionCreate}
            onDeleteAll={onDeleteAll}
            onAddAll={onAddAll}
            accessList={accessList.map((item) => <AccessUser onDelete={() => onPermissionDelete(item.id)} key={item.id} name={item.name} type={item.type} id={item.id} />)}
            accessData={accessList}
          />
        </div>
      )}
    </ModalBig>
  )
}

CreateFolderModal.propTypes = {
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  confirmButtonLabel: PropTypes.string,
  open: PropTypes.bool,
  value: PropTypes.string,
  emptyErrorMessage: PropTypes.string,
  invalidCharacters: PropTypes.array,
}

CreateFolderModal.defaultProps = {
  open: false,
}

export default CreateFolderModal
